import { miloHandlerForCase } from "../controller/cases";
import {
  miloHandlerForCallback,
  contactEstablished,
} from "../controller/callback";
import {
  miloHandlerForChat,
  setTransferEndpoints as setTransferEndpointsForChat,
} from "../controller/chat";
import { miloHandler, setTransferEndpoints } from "../controller/call";
import { miloHandlerForVoicemail } from "../controller/voicemail";
import { SessionEvent } from "../types/call.type";
import {
  speakerIcon1,
  speakerIcon2,
  speakerIcon3,
} from "../components/common/Icons";
import digitalRing from "./audio/digital-ring.mp3";

type NotificationOptions = {
  body?: string;
  icon?: string;
  onclick?: () => void;
};

class NotificationManager {
  private static instance: NotificationManager;
  private workerPort?: MessagePort;
  private isOwner = false;
  private originalFavicon: string = "";
  private originalTitle: string = document.title;
  private faviconInterval?: NodeJS.Timeout;
  private titleInterval?: NodeJS.Timeout;
  private icons = [speakerIcon1, speakerIcon2, speakerIcon3];
  private rotatingTitle = ["Contact Ringing..."];
  private chatMessageAudio: HTMLAudioElement | null = null;

  private miloHandlerMap: Record<string, (ev: SessionEvent<any>) => void> = {
    call: miloHandler,
    callback: miloHandlerForCallback,
    voicemail: miloHandlerForVoicemail,
    case: miloHandlerForCase,
    chat: miloHandlerForChat,
  };

  private constructor() {
    this.initWorker();
    window.addEventListener("beforeunload", () => {
      this.workerPort?.postMessage({ type: "DISCONNECT" });
    });
  }

  public static getInstance(): NotificationManager {
    if (!this.instance) {
      this.instance = new NotificationManager();
    }
    return this.instance;
  }

  private initWorker() {
    if (typeof SharedWorker === "undefined") {
      console.warn("SharedWorker not supported in this browser.");
      return;
    }

    try {
      const notificationWorker = new SharedWorker(
        new URL("../notificationWorker.js", import.meta.url),
        { name: "notification-coordinator" }
      );
      this.workerPort = notificationWorker.port;

      this.workerPort.onmessage = (event) => {
        if (event.data?.type === "BROWSER_NOTIFICATION") {
          this.isOwner = true;
        }
      };

      this.workerPort.start();
    } catch (err) {
      console.error("Failed to initialize SharedWorker", err);
    }
  }

  public async eventLoggingAPI(
    ev: SessionEvent<any>,
    handler: (ev: SessionEvent<any>) => void
  ): Promise<void> {
    if (!this.isOwner) return;

    // const handlerMap: Record<string, (ev: SessionEvent<any>) => void> = {
    //   call: contactEventHandler,
    //   callback: contactEventHandler,
    //   voicemail: contactEventHandler,
    //   chat: contactEventHandler,
    // };

    // const handler = handlerMap[type];
    if (handler) {
      handler(ev);
    }
  }

  public async triggerMilo(ev: SessionEvent<any>, type: string): Promise<void> {
    const actions: Record<string, (contactId?: string) => void> = {
      call: (contactId) => setTransferEndpoints(contactId),
      callback: (contactId) => contactEstablished(contactId),
      voicemail: (contactId) => contactEstablished(contactId),
      chat: (contactId) => setTransferEndpointsForChat(contactId),
    };

    //for setting transfer endpoints
    actions[type]?.(ev.body?.contactId);

    if (!this.isOwner) return;

    this.miloHandlerMap[type]?.(ev);
  }

  public async playRingtone(ringType: string): Promise<void> {
    if (!this.isOwner) return;

    // Only handle MESSAGE ringtone (chat messages) here
    // Other ringtones are handled by individual controllers
    if (ringType === "MESSAGE") {
      try {
        if (!this.chatMessageAudio) {
          this.chatMessageAudio = new Audio(digitalRing);
          this.chatMessageAudio.loop = false;
          this.chatMessageAudio.volume = 0.9;
        }
        this.chatMessageAudio.currentTime = 0;
        this.chatMessageAudio.play().catch(() => {});
      } catch (e) {
        // Silently fail if audio can't play
      }
    }
  }

  public getIsOwner(): boolean {
    return this.isOwner;
  }

  //Fix for multi tab scenario, if one tabs logs out
  public releaseOwnership(): void {
    if (this.isOwner) {
      this.isOwner = false;
      this.workerPort?.postMessage({ type: "RELEASE_OWNERSHIP" });
      this.stopFaviconBlinking();

      // Stop audio if playing
      if (this.chatMessageAudio) {
        this.chatMessageAudio.pause();
        this.chatMessageAudio.currentTime = 0;
      }
    }
  }

  public startFaviconBlinking() {
    const link =
      document.querySelector("link[rel~='icon']") ||
      document.createElement("link");

    link.setAttribute("rel", "icon");
    link.setAttribute("type", "image/svg+xml");

    if (!link.parentNode) {
      document.head.appendChild(link);
    }

    // Only set originalFavicon once
    if (!this.originalFavicon && (link as HTMLLinkElement).href) {
      this.originalFavicon = (link as HTMLLinkElement).href;
    }

    let iconIndex = 0;
    this.faviconInterval = setInterval(() => {
      (link as HTMLLinkElement).href = this.icons[iconIndex];
      iconIndex = (iconIndex + 1) % this.icons.length;
    }, 600);

    // --- Rotating between array messages and original title ---
    const frames = [...this.rotatingTitle, this.originalTitle];
    let titleIndex = 0;

    this.titleInterval = setInterval(() => {
      document.title = frames[titleIndex];
      titleIndex = (titleIndex + 1) % frames.length;
    }, 1000);
  }

  public stopFaviconBlinking() {
    clearInterval(this.faviconInterval);
    this.faviconInterval = undefined;

    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (link && this.originalFavicon) {
      link.href = this.originalFavicon;
    }

    clearInterval(this.titleInterval);
    this.titleInterval = undefined;
    document.title = this.originalTitle;
  }

  public async show(
    title: string,
    options?: NotificationOptions
  ): Promise<void> {
    if (!("Notification" in window)) {
      console.warn("Notifications are not supported.");
      return;
    }

    if (!this.isOwner) return;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const notification = new Notification(title, {
      body: options?.body,
      icon: options?.icon,
    });

    if (options?.onclick) {
      notification.onclick = () => {
        options.onclick?.();
        notification.close();
        this.stopFaviconBlinking();
      };
    }
  }
}

export const notificationManager = NotificationManager.getInstance();
